import { useEffect, useMemo, useRef, useState } from "react";
import {
  FaBuilding,
  FaChevronDown,
  FaExclamationTriangle,
  FaGlobe,
} from "react-icons/fa";
import AppHeaderBar from "./AppHeaderBar";
import AppNavBar from "./AppNavBar";
import PushHealthAlert from "./PushHealthAlert";
import { requestPermission } from "../firebase";
import { reportPushDiagnostics } from "../services/pushDiagnostics";
import Cookies from "js-cookie";
import {
  updateUserCorporation,
  updateUserFcmToken,
  updateUserLanguage,
  deleteUserFcmToken,
  getUserData,
} from "../services/users";
import {
  hasCharacterizationModule,
  hasMonitoringModule,
} from "./mobileModuleUtils";
import { CustomSwitch } from "../components/CustomSwitch";
import { useDispatch, useSelector } from "react-redux";
import { fetchUserLanguage } from "../redux/languageSlice.js";
import { languageData } from "../shared/utils/utils.js";
import {
  PUSH_HEALTH,
  getPushHealth,
  getPushHealthMessageKey,
  syncSessionUserNotificationPrefs,
  mergeSessionUserPartial,
  hasAnyPushPref,
} from "../shared/utils/pushHealth";
import { useTranslation } from "react-i18next";
import NotificationTestPanel from "../containers/userAccess/NotificationTestPanel";

const getSettingsChevronClassName = (isOpen) =>
  isOpen
    ? "mobile-settings-row-chevron mobile-settings-row-chevron-open"
    : "mobile-settings-row-chevron";

const SettingsSelectBlock = ({
  panelRef,
  icon,
  iconClassName,
  title,
  value,
  isOpen,
  onToggle,
  options,
  isOptionSelected,
  onSelectOption,
  renderOptionLeading,
  disabled,
}) => (
  <div ref={panelRef}>
    <button
      type="button"
      className="mobile-settings-row mobile-settings-row-action"
      onClick={onToggle}
      disabled={disabled}
      aria-expanded={isOpen}
    >
      <span className={`mobile-settings-row-icon ${iconClassName}`} aria-hidden>
        {icon}
      </span>
      <span className="mobile-settings-row-body">
        <span className="mobile-settings-row-title">{title}</span>
        <span className="mobile-settings-row-value">{value}</span>
      </span>
      <FaChevronDown
        className={getSettingsChevronClassName(isOpen)}
        aria-hidden
      />
    </button>

    {isOpen && (
      <div className="mobile-settings-language-list">
        {options.map((option) => (
          <button
            key={option.key}
            type="button"
            className={
              isOptionSelected(option)
                ? "mobile-settings-language-option mobile-settings-language-option-selected"
                : "mobile-settings-language-option"
            }
            onClick={() => onSelectOption(option)}
            disabled={disabled}
          >
            {renderOptionLeading?.(option)}
            <span>{option.label}</span>
          </button>
        ))}
      </div>
    )}
  </div>
);

const readStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user")) || null;
  } catch {
    return null;
  }
};

const AppSettings = ({ setCookies, saveUser }) => {
  const fcmToken = Cookies.get("fcmToken");
  const [user, setUser] = useState(() => readStoredUser());
  const { t, i18n } = useTranslation();
  const [anomalyPush, setAnomalyPush] = useState(
    Boolean(user?.receiveAnomalyPush),
  );
  const [characterizationPush, setCharacterizationPush] = useState(
    Boolean(user?.recieveCharacterizationPush),
  );
  const [qualicheckPush, setQualicheckPush] = useState(
    Boolean(user?.recieveQualicheckPush),
  );
  const [fillingPush, setFillingPush] = useState(
    Boolean(user?.receiveFillingPush),
  );
  const [loading, setLoading] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [corporationOpen, setCorporationOpen] = useState(false);
  const [permissionError, setPermissionError] = useState("");
  const [pushHealthStatus, setPushHealthStatus] = useState(PUSH_HEALTH.IDLE);
  const language = useSelector((state) => state.language.data);
  const dispatch = useDispatch();
  const languagePanelRef = useRef(null);
  const corporationPanelRef = useRef(null);

  const showCorporationSelector = (user?.multiCorporations?.length ?? 0) > 1;

  const prefsSnapshot = useMemo(
    () => ({
      receiveAnomalyPush: anomalyPush,
      recieveCharacterizationPush: characterizationPush,
      recieveQualicheckPush: qualicheckPush,
      receiveFillingPush: fillingPush,
      receiveRotationPush: Boolean(user?.receiveRotationPush),
    }),
    [
      anomalyPush,
      characterizationPush,
      fillingPush,
      qualicheckPush,
      user?.receiveRotationPush,
    ],
  );

  const { activeCorporation, corporationOptions } = useMemo(() => {
    if (!showCorporationSelector) {
      return { activeCorporation: null, corporationOptions: [] };
    }

    const options = user.multiCorporations.map((item) => ({
      key: item._id,
      value: item._id,
      label: item.corporationName,
    }));

    return {
      activeCorporation:
        user.multiCorporations.find(
          (item) => String(item._id) === String(user.corporation),
        ) || null,
      corporationOptions: options,
    };
  }, [showCorporationSelector, user?.corporation, user?.multiCorporations]);

  const languageOptions = useMemo(
    () =>
      languageData.map((item) => ({
        key: item._id,
        value: item.value,
        label: item.label,
        flag: item.flag,
      })),
    [],
  );

  // Show monitoring/characterization rows when the module is on, or when the
  // preference is already enabled (e.g. set from the web Users matrix).
  const showMonitoringNotifications =
    hasMonitoringModule() || anomalyPush || fillingPush;
  const showCharacterizationNotifications =
    hasCharacterizationModule() || characterizationPush || qualicheckPush;
  const showNotificationsSection =
    showMonitoringNotifications || showCharacterizationNotifications;

  const notificationRows = useMemo(() => {
    const rows = [];
    if (showMonitoringNotifications) {
      rows.push({
        key: "anomaly",
        title: t("mobile_settings_notify_sort"),
        checked: anomalyPush,
      });
      rows.push({
        key: "filling_level",
        title: t("mobile_settings_notify_fill"),
        checked: fillingPush,
      });
    }
    if (showCharacterizationNotifications) {
      rows.push({
        key: "characterization",
        title: t("mobile_settings_notify_wq"),
        checked: characterizationPush,
      });
      rows.push({
        key: "qualicheck",
        title: t("mobile_settings_notify_wqc"),
        checked: qualicheckPush,
      });
    }
    return rows;
  }, [
    anomalyPush,
    characterizationPush,
    fillingPush,
    qualicheckPush,
    showCharacterizationNotifications,
    showMonitoringNotifications,
    t,
  ]);

  const refreshPushHealth = () => {
    const health = getPushHealth({
      user: prefsSnapshot,
      fcmTokenCookie: Cookies.get("fcmToken"),
    });
    setPushHealthStatus(health.status);
    if (health.unhealthy) {
      const key = getPushHealthMessageKey(health.status);
      setPermissionError(key ? t(key) : "");
    } else {
      setPermissionError("");
    }
  };

  const applyPushPrefsFromUser = (nextUser) => {
    setAnomalyPush(Boolean(nextUser?.receiveAnomalyPush));
    setCharacterizationPush(Boolean(nextUser?.recieveCharacterizationPush));
    setQualicheckPush(Boolean(nextUser?.recieveQualicheckPush));
    setFillingPush(Boolean(nextUser?.receiveFillingPush));
  };

  const persistUser = (nextUser) => {
    const merged = mergeSessionUserPartial(nextUser) || nextUser;
    setUser(merged);
  };

  useEffect(() => {
    const handleSessionNotificationsUpdated = (event) => {
      const nextUser = event?.detail?.user;
      if (!nextUser) return;
      applyPushPrefsFromUser(nextUser);
      persistUser({
        ...(readStoredUser() || {}),
        ...nextUser,
      });
    };
    window.addEventListener(
      "sessionUserNotificationsUpdated",
      handleSessionNotificationsUpdated,
    );
    return () => {
      window.removeEventListener(
        "sessionUserNotificationsUpdated",
        handleSessionNotificationsUpdated,
      );
    };
  }, []);

  // Prefs may have been set from the web Users matrix; localStorage can be stale.
  useEffect(() => {
    const userId = readStoredUser()?._id;
    if (!userId) return undefined;

    let cancelled = false;
    const refreshUserPrefs = async () => {
      try {
        const response = await getUserData(userId);
        const fresh = response?.data?.user;
        if (cancelled || !fresh) return;

        applyPushPrefsFromUser(fresh);
        const synced = syncSessionUserNotificationPrefs(fresh);
        const current = readStoredUser() || {};
        const next = synced || {
          ...current,
          ...fresh,
          token: current.token || fresh.token,
        };
        persistUser(next);
        if (typeof saveUser === "function") {
          await saveUser(next);
        }
      } catch (error) {
        console.error("Failed to refresh user push prefs:", error);
      }
    };

    void refreshUserPrefs();
    return () => {
      cancelled = true;
    };
    // Refresh once on mount (and if saveUser identity changes).
  }, [saveUser]);

  useEffect(() => {
    refreshPushHealth();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh when prefs / token change
  }, [prefsSnapshot, fcmToken, t]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        languagePanelRef.current &&
        !languagePanelRef.current.contains(event.target)
      ) {
        setLanguageOpen(false);
      }
      if (
        corporationPanelRef.current &&
        !corporationPanelRef.current.contains(event.target)
      ) {
        setCorporationOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const changeLanguage = async (item) => {
    setLoading(true);
    setLanguageOpen(false);
    try {
      await updateUserLanguage({ language: item.value }, user._id);
      localStorage.setItem("language", item.value);
      dispatch(fetchUserLanguage(user._id));
      i18n.changeLanguage(item.value);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const changeCorporation = async (corporationId) => {
    setLoading(true);
    setCorporationOpen(false);
    try {
      const response = await updateUserCorporation(
        { corporation: corporationId },
        user._id,
      );
      await saveUser(response.data);
      window.location.reload();
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const handleEnablePushOnDevice = async () => {
    setLoading(true);
    setPermissionError("");
    try {
      const permission = await requestPermission(setLoading, null);
      if (permission === "granted") {
        refreshPushHealth();
        void reportPushDiagnostics();
        return;
      }
      const health = getPushHealth({
        user: prefsSnapshot,
        fcmTokenCookie: Cookies.get("fcmToken"),
      });
      const key = getPushHealthMessageKey(health.status);
      setPermissionError(key ? t(key) : t("push.notifications.blocked"));
      setPushHealthStatus(health.status);
    } catch (error) {
      console.error(error);
      setPermissionError(t("push.notifications.blocked"));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = async (event, type) => {
    const isChecked = event.target.checked;

    const updateStateMap = {
      anomaly: setAnomalyPush,
      characterization: setCharacterizationPush,
      qualicheck: setQualicheckPush,
      filling_level: setFillingPush,
    };

    const pushFieldMap = {
      anomaly: "receiveAnomalyPush",
      characterization: "recieveCharacterizationPush",
      qualicheck: "recieveQualicheckPush",
      filling_level: "receiveFillingPush",
    };

    try {
      if (isChecked) {
        updateStateMap[type]?.(true);
        setLoading(true);

        const { data } = await updateUserFcmToken({
          [pushFieldMap[type]]: true,
        });
        persistUser(data);

        const permission = await requestPermission(setLoading, type);

        if (permission === "granted") {
          setPermissionError("");
          refreshPushHealth();
          return;
        }

        // Keep intention ON; surface device health instead of rolling back.
        const health = getPushHealth({
          user: { ...prefsSnapshot, [pushFieldMap[type]]: true },
          fcmTokenCookie: Cookies.get("fcmToken"),
        });
        setPushHealthStatus(health.status);
        const key = getPushHealthMessageKey(health.status);
        setPermissionError(key ? t(key) : t("push.notifications.blocked"));
      } else {
        setLoading(true);
        updateStateMap[type]?.(false);

        const remainingPrefs = {
          receiveAnomalyPush: type === "anomaly" ? false : anomalyPush,
          recieveCharacterizationPush:
            type === "characterization" ? false : characterizationPush,
          recieveQualicheckPush: type === "qualicheck" ? false : qualicheckPush,
          receiveFillingPush: type === "filling_level" ? false : fillingPush,
        };

        const payload = {
          [pushFieldMap[type]]: false,
        };

        const { data } = await updateUserFcmToken(payload);
        persistUser(data);

        // Keep the device token while other push prefs remain enabled.
        if (!hasAnyPushPref(remainingPrefs) && fcmToken) {
          await deleteUserFcmToken(fcmToken);
          Cookies.remove("fcmToken");
        }
        setPermissionError("");
      }
    } catch (error) {
      console.error("Notification preference update failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const showHealthBanner =
    pushHealthStatus !== PUSH_HEALTH.IDLE &&
    pushHealthStatus !== PUSH_HEALTH.HEALTHY;

  return (
    <div className="installation-list-page mobile-settings-page">
      <AppHeaderBar
        title={t("Settings")}
        setCookies={setCookies}
        saveUser={saveUser}
      />

      <main className="mobile-settings-main">
        <section className="installation-subpage-card mobile-settings-card">
          <h2 className="mobile-settings-section-title">
            {t("mobile_settings_section_general")}
          </h2>

          <div className="mobile-settings-language">
            <SettingsSelectBlock
              panelRef={languagePanelRef}
              icon={<FaGlobe />}
              iconClassName="mobile-settings-row-icon-language"
              title={t("mobile_settings_language")}
              value={language?.label || "—"}
              isOpen={languageOpen}
              onToggle={() => {
                setCorporationOpen(false);
                setLanguageOpen((open) => !open);
              }}
              options={languageOptions}
              isOptionSelected={(option) => language?.value === option.value}
              onSelectOption={(option) => void changeLanguage(option)}
              renderOptionLeading={(option) => (
                <img
                  className="mobile-settings-language-flag"
                  src={option.flag}
                  alt=""
                />
              )}
              disabled={loading}
            />
          </div>

          {showCorporationSelector && (
            <div className="mobile-settings-corporation">
              <SettingsSelectBlock
                panelRef={corporationPanelRef}
                icon={<FaBuilding />}
                iconClassName="mobile-settings-row-icon-corporation"
                title={t("mobile_settings_corporation")}
                value={activeCorporation?.corporationName || "—"}
                isOpen={corporationOpen}
                onToggle={() => {
                  setLanguageOpen(false);
                  setCorporationOpen((open) => !open);
                }}
                options={corporationOptions}
                isOptionSelected={(option) =>
                  String(activeCorporation?._id) === String(option.value)
                }
                onSelectOption={(option) =>
                  void changeCorporation(option.value)
                }
                disabled={loading}
              />
            </div>
          )}
        </section>

        {showNotificationsSection && (
          <section className="installation-subpage-card mobile-settings-card mobile-settings-card-compact">
            <h2 className="mobile-settings-section-title mobile-settings-section-title-inline">
              {t("mobile_settings_section_notifications")}
            </h2>

            {showHealthBanner && (
              <PushHealthAlert
                status={pushHealthStatus}
                enableLoading={loading}
                onEnable={
                  pushHealthStatus === PUSH_HEALTH.NEEDS_PERMISSION ||
                  pushHealthStatus === PUSH_HEALTH.TOKEN_MISSING
                    ? () => void handleEnablePushOnDevice()
                    : undefined
                }
                className="mobile-settings-push-health"
              />
            )}

            <ul className="mobile-settings-notify-list">
              {notificationRows.map((row) => (
                <li key={row.key} className="mobile-settings-notify-item">
                  <span className="mobile-settings-notify-label">
                    {row.title}
                    {row.checked && showHealthBanner ? (
                      <span className="mobile-settings-notify-device-hint">
                        {t("push.health.notDeliverableOnDevice")}
                      </span>
                    ) : null}
                  </span>
                  <CustomSwitch
                    className="mobile-settings-switch"
                    checked={row.checked}
                    disabled={loading}
                    onChange={(event) => void handleChange(event, row.key)}
                  />
                </li>
              ))}
            </ul>

            <NotificationTestPanel variant="mobile" />
          </section>
        )}

        {permissionError && !showHealthBanner && (
          <div
            className="mobile-settings-alert mobile-settings-alert-error"
            role="alert"
          >
            <FaExclamationTriangle aria-hidden />
            <span>{permissionError}</span>
          </div>
        )}
      </main>

      {loading && (
        <div
          className="mobile-settings-loading"
          aria-busy="true"
          aria-live="polite"
        >
          <div className="installation-detail-spinner" aria-hidden />
        </div>
      )}

      <AppNavBar />
    </div>
  );
};

export default AppSettings;
